/**
 * SRV-F2-3: Worksheet Basket Sidebar
 * Több munkalap összegyűjtése kosárba → konszolidált számla generálás.
 * Backend: warranty-billing.controller.ts basket endpoints.
 */

import { api } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useTranslation } from '@kgc/i18n';
import { useCallback, useEffect, useState } from 'react';

import { isWarrantyBasketError } from './warranty-basket-error';

interface BasketItem {
  worksheetId: string;
  worksheetNumber: string;
  partnerName: string;
  totalAmount: number;
  addedAt: string;
}

interface BasketResponse {
  items: BasketItem[];
  totalNet: number;
  totalGross: number;
  itemCount: number;
}

const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat('hu-HU', {
    style: 'currency',
    currency: 'HUF',
    maximumFractionDigits: 0,
  }).format(amount);

export function WorksheetBasketSidebar() {
  const { t } = useTranslation();
  const [basket, setBasket] = useState<BasketResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [addInput, setAddInput] = useState('');
  const [adding, setAdding] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const loadBasket = useCallback(async () => {
    try {
      const res = await api.get<{ data: BasketResponse }>('/service/basket');
      setBasket(res.data);
    } catch {
      // Non-critical
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadBasket();
  }, [loadBasket]);

  const handleAdd = async () => {
    if (!addInput.trim()) return;
    setAdding(true);
    setError(null);
    setSuccessMsg(null);
    try {
      await api.post('/service/basket/add', { worksheetId: addInput.trim() });
      setAddInput('');
      await loadBasket();
    } catch (err) {
      setError(
        isWarrantyBasketError(err)
          ? t('worksheet.basket.warrantyError')
          : err instanceof Error
            ? err.message
            : t('common.error')
      );
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (worksheetId: string) => {
    try {
      await api.delete(`/service/basket/${worksheetId}`);
      await loadBasket();
    } catch {
      // Silently handle
    }
  };

  const handleCheckout = async () => {
    setCheckingOut(true);
    setError(null);
    try {
      const res = await api.post<{ data: { invoiceId: string; invoiceNumber: string } }>(
        '/service/basket/checkout',
        {}
      );
      setSuccessMsg(`${t('worksheet.basket.checkoutSuccess')}: ${res.data.invoiceNumber}`);
      await loadBasket();
    } catch (err) {
      setError(
        isWarrantyBasketError(err)
          ? t('worksheet.basket.warrantyError')
          : err instanceof Error
            ? err.message
            : t('common.error')
      );
    } finally {
      setCheckingOut(false);
    }
  };

  const handleClear = async () => {
    try {
      await api.delete('/service/basket');
      await loadBasket();
    } catch {
      // Silently handle
    }
  };

  const itemCount = basket?.itemCount ?? 0;

  if (loading) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center justify-between">
          <span>
            {t('worksheet.basket.title')}
            {itemCount > 0 && (
              <span className="ml-2 inline-flex items-center justify-center rounded-full bg-blue-600 px-2 py-0.5 text-xs font-medium text-white">
                {itemCount}
              </span>
            )}
          </span>
          {itemCount > 0 && (
            <Button
              size="sm"
              variant="ghost"
              className="text-red-500 text-xs"
              onClick={handleClear}
            >
              {t('worksheet.basket.clear')}
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {error && <p className="text-sm text-red-500">{error}</p>}
        {successMsg && <p className="text-sm text-green-600 dark:text-green-400">{successMsg}</p>}

        {/* Add to basket */}
        <div className="flex gap-2">
          <Input
            placeholder={t('worksheet.basket.addPlaceholder')}
            value={addInput}
            onChange={e => setAddInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
            className="text-sm"
          />
          <Button size="sm" onClick={handleAdd} disabled={adding || !addInput.trim()}>
            {adding ? '...' : '+'}
          </Button>
        </div>

        {/* Basket items */}
        {itemCount === 0 ? (
          <p className="text-sm text-muted-foreground">{t('worksheet.basket.empty')}</p>
        ) : (
          <>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {basket?.items.map(item => (
                <div
                  key={item.worksheetId}
                  className="flex items-center justify-between p-1.5 rounded border border-gray-100 dark:border-gray-800 text-sm"
                >
                  <div className="min-w-0">
                    <span className="font-mono text-xs">{item.worksheetNumber}</span>
                    <span className="ml-2 text-muted-foreground text-xs">
                      {formatCurrency(item.totalAmount)}
                    </span>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 w-6 p-0 text-red-400"
                    onClick={() => handleRemove(item.worksheetId)}
                  >
                    ×
                  </Button>
                </div>
              ))}
            </div>

            {/* Totals + checkout */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-2">
              <div className="flex justify-between text-sm font-medium">
                <span>{t('common.total')}</span>
                <span>{formatCurrency(basket?.totalGross ?? 0)}</span>
              </div>
              <Button className="w-full mt-2" onClick={handleCheckout} disabled={checkingOut}>
                {checkingOut ? '...' : t('worksheet.basket.checkout')}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
